<template>
  <div class="address-ac">
    <input
      ref="input"
      type="text"
      class="form-control"
      :class="{ 'is-invalid': invalid }"
      :placeholder="placeholder"
      autocomplete="off"
      @input="onInput"
      @keydown="onKeydown"
      @blur="open = false"
    >
    <ul
      v-if="open && items.length"
      class="address-ac__menu"
    >
      <template v-for="(item, i) in items">
        <li
          v-if="i > 0 && !item.inside && items[i - 1].inside"
          :key="'sep-' + item.place_id"
          class="address-ac__separator"
        >
          {{ $t('occurrence.panel.addressOutsideCoverage') }}
        </li>
        <li
          :key="item.place_id"
          class="address-ac__item"
          :class="{ active: i === active }"
          @mousedown.prevent="select(item)"
          @mouseenter="active = i"
        >
          <svg
            class="address-ac__pin"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
          </svg>
          <span class="address-ac__text">
            <span
              v-for="(part, k) in item.mainParts"
              :key="k"
              :class="{ 'address-ac__match': part.match }"
            >{{ part.text }}</span>
            <span class="address-ac__secondary">{{ item.secondary }}</span>
          </span>
        </li>
      </template>
      <li class="address-ac__powered" />
    </ul>
  </div>
</template>

<script>
/**
 * Campo de endereço com sugestões do Google, de acordo com o modo do nó:
 * - free: sugestões comuns;
 * - prioritize: busca duas vezes — só dentro do retângulo da abrangência (`locationRestriction`)
 *   e com preferência por ele (`locationBias`) — e mostra primeiro os de dentro: o Google
 *   sozinho só "prefere" a área e ainda põe ruas famosas de outras cidades na frente;
 * - only: só os de dentro do retângulo.
 *
 * Emite `place` com o lugar escolhido (geometria e componentes do endereço), como o
 * campo antigo. `$refs.input` continua sendo o <input>, para quem escreve o texto de fora.
 */
export default {
  name: 'AddressAutocomplete',
  props: {
    // { south, west, north, east } da abrangência; sem ele, o modo não faz efeito.
    bounds: { type: Object, default: null },
    // free | prioritize | only
    mode: { type: String, default: 'prioritize' },
    placeholder: { type: String, default: '' },
    invalid: { type: Boolean, default: false },
  },
  data() {
    return { items: [], open: false, active: -1 }
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    onInput(event) {
      const text = event.target.value.trim()
      clearTimeout(this.timer)
      this.search_id = (this.search_id || 0) + 1

      if (text.length < 3) {
        this.items = []
        this.open = false
        return
      }

      this.timer = setTimeout(() => this.search(text), 250)
    },

    async search(text) {
      const searchId = this.search_id
      await this.$gmapApiPromiseLazy()
      const { maps } = window.google

      this.service = this.service || new maps.places.AutocompleteService()
      this.token = this.token || new maps.places.AutocompleteSessionToken()

      const predict = extra => new Promise(resolve => {
        this.service.getPlacePredictions(
          { input: text, sessionToken: this.token, ...extra },
          (results, status) => resolve(status === 'OK' && results ? results : []),
        )
      })

      let inside = []
      let outside = []
      if (this.bounds && this.mode !== 'free') {
        const bounds = new maps.LatLngBounds(
          { lat: this.bounds.south, lng: this.bounds.west },
          { lat: this.bounds.north, lng: this.bounds.east },
        )
        // `strictBounds` só existe no campo pronto do Google; para o serviço, a restrição é `locationRestriction`.
        if (this.mode === 'only') {
          inside = await predict({ locationRestriction: bounds })
        } else {
          ;[inside, outside] = await Promise.all([
            predict({ locationRestriction: bounds }),
            predict({ locationBias: bounds }),
          ])
        }
      } else {
        outside = await predict({})
      }

      // Uma busca mais nova já foi pedida enquanto esta respondia.
      if (searchId !== this.search_id) return

      const insideIds = new Set(inside.map(p => p.place_id))
      const toItem = (p, isInside) => {
        const sf = p.structured_formatting
        const main = sf ? sf.main_text : p.description

        return {
          place_id: p.place_id,
          description: p.description,
          mainParts: this.highlight(main, sf ? sf.main_text_matched_substrings : []),
          secondary: sf ? sf.secondary_text : '',
          inside: isInside,
        }
      }

      this.items = [
        ...inside.map(p => toItem(p, true)),
        ...outside.filter(p => !insideIds.has(p.place_id)).map(p => toItem(p, false)),
      ]
      this.active = -1
      this.open = this.items.length > 0
    },

    // Quebra o texto em trechos, marcando os que o Google diz casar com o que foi digitado.
    highlight(text, matches) {
      const parts = []
      let cursor = 0
      ;(matches || []).forEach(({ offset, length }) => {
        if (offset > cursor) parts.push({ text: text.slice(cursor, offset), match: false })
        parts.push({ text: text.slice(offset, offset + length), match: true })
        cursor = offset + length
      })
      if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false })

      return parts
    },

    onKeydown(event) {
      if (!this.open || !this.items.length) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.active = (this.active + 1) % this.items.length
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.active = (this.active - 1 + this.items.length) % this.items.length
      } else if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault()
        this.select(this.items[this.active])
      } else if (event.key === 'Escape') {
        this.open = false
      }
    },

    select(item) {
      this.open = false
      this.search_id = (this.search_id || 0) + 1
      this.$refs.input.value = item.description

      const { maps } = window.google
      const places = new maps.places.PlacesService(document.createElement('div'))
      places.getDetails(
        {
          placeId: item.place_id,
          fields: ['geometry', 'address_components', 'formatted_address'],
          sessionToken: this.token,
        },
        (place, status) => {
          // A sessão de cobrança do Google termina ao escolher um lugar.
          this.token = null
          if (status === 'OK' && place) this.$emit('place', place)
        },
      )
    },
  },
}
</script>

<style scoped>
.address-ac {
  position: relative;
}

/* Lista no estilo da do próprio Google: ícone de local, trecho digitado em negrito, endereço em cinza. */
.address-ac__menu {
  position: absolute;
  top: calc(100% + 1px);
  left: 0;
  right: 0;
  z-index: 1060;
  max-height: 340px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  font-family: Roboto, Arial, sans-serif;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-top: 0;
  border-radius: 0 0 2px 2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.address-ac__item {
  display: flex;
  align-items: center;
  padding: 0 10px;
  height: 30px;
  font-size: 14px;
  color: #000;
  cursor: pointer;
}

.address-ac__item.active {
  background: #ebebeb;
}

.address-ac__pin {
  flex: none;
  margin-right: 8px;
  fill: #999;
}

.address-ac__text {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.address-ac__match {
  font-weight: 700;
}

.address-ac__secondary {
  margin-left: 6px;
  font-size: 12px;
  color: #777;
}

.address-ac__separator {
  padding: 8px 10px 3px;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #777;
  border-top: 1px solid #e6e6e6;
}

.address-ac__powered {
  height: 22px;
  background: #fff url('https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png') no-repeat right 8px center;
  background-size: 120px 14px;
  border-top: 1px solid #e6e6e6;
}

.dark-layout .address-ac__menu {
  background: #283046;
  border-color: #3b4253;
}

.dark-layout .address-ac__item {
  color: #d0d2d6;
}

.dark-layout .address-ac__item.active {
  background: #343d55;
}

.dark-layout .address-ac__secondary,
.dark-layout .address-ac__separator {
  color: #b4b7bd;
}

.dark-layout .address-ac__powered {
  background-color: #283046;
  background-image: url('https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-non-white3.png');
  border-color: #3b4253;
}
</style>
